import React, { useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useDispatch } from 'react-redux'
import moment, { Moment } from '@e3dc-react/shell/Libs/moment'
import Joi from 'joi'
import { Grid } from '@mui/material'
import { makeStyles } from 'tss-react/mui'

import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { DatePicker, TextField } from '@e3dc-react/shell/Elements/InputFields'
import { useValidation } from '@e3dc-react/shell/Hooks/useValidation/useValidation'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'

import { updateUserData } from '../../State/User/UserActions'
import { IPinFormModel } from '../../State/User/UserModel'

const useStyles = makeStyles()(theme => ({
  wrapper: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
}))

interface IPinEditorProps {
  userId: number
  onClose: () => void
  place: number
}

const validationSchema = Joi.object().keys({
  ipin: Joi.string().required(),
  certificationDate: Joi.string().required(),
})

const IPinEditor: React.FC<IPinEditorProps> = ({ userId, place, onClose }) => {
  const { t } = useCustomTranslation()
  const [isSaving, setIsSaving] = useState(false)
  const [validationActive, setValidationActive] = useState(false)
  const [iPinForm, setIPinForm] = useState<IPinFormModel>({
    certificationDate: new Date().toISOString(),
  })
  const authFetch = useAuthFetchWithResult()
  const validationObj = useValidation(iPinForm, validationSchema)
  const { classes } = useStyles()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const dispatch = useDispatch()

  const validation = validationActive ? validationObj : {}

  const saveIpin = (): void => {
    setIsSaving(true)
    setValidationActive(true)

    if (Object.keys(validationObj).length === 0) {
      authFetch({
        path: `users/${userId}`,
        method: 'PUT',
        payload: { ipin: iPinForm.ipin, level: 'INSTALLER' },
      })
        .then(() => {
          setIsSaving(false)
          setValidationActive(false)
          enqueueSuccessStack(t('user.save.ipinSuccess'))
          dispatch(updateUserData({ ipin: iPinForm.ipin }))
          onClose()
        })
        .catch(() => {
          setIsSaving(false)
          setValidationActive(false)
          enqueueErrorStack(t('user.save.ipinError'))
        })
    }

    //todo checken ob der ipin zum angegebenen user gehört über datum der Prüfung und Prüfungstabelle, die noch kommt!
  }

  const onDateChange = (value: Moment | null): void => {
    setIPinForm({ ...iPinForm, certificationDate: value?.toISOString() })
  }

  const onIpinChange = (value: string): void => {
    setIPinForm({ ...iPinForm, ipin: value })
  }

  return (
    <Dialog
      onClose={() => onClose()}
      title={t('user.userData.registerIPin')}
      actions={{
        primary: {
          name: t('common.save'),
          disabled: validationActive && Object.keys(validationObj).length > 0,
          onClick: () => saveIpin(),
          isLoading: isSaving,
        },
        secondary: [
          {
            name: t('common.cancel'),
          },
        ],
      }}
      place={place}
    >
      <Grid container className={classes.wrapper}>
        <Grid item xs={12}>
          <TextField
            label={t('user.userData.ipin')}
            value={iPinForm?.ipin}
            onChange={onIpinChange}
            helperText={validation['ipin'] ? validation['ipin'].message : undefined}
          />
        </Grid>
        <Grid item xs={12}>
          <DatePicker
            label={t('user.userData.certificationDate')}
            onChange={onDateChange}
            value={moment(iPinForm?.certificationDate)}
            helperText={validation['certificationDate'] ? validation['certificationDate'].message : undefined}
          />
        </Grid>
      </Grid>
    </Dialog>
  )
}

export default IPinEditor
